import { useState } from 'react';
import { getFromStorage, storeInLocal } from 'src/lib/storage';
import {
  ScheduleOutlined,
  SettingOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import {
  useFeatureEntitlements,
  usePermissionEntitlements,
  useAuth,
} from '@frontegg/react';
import { ArrowNarrowRight } from '@insight-health-ai/roseate/icons';
import { useGetCurrentPractitioner } from 'src/hooks/settings';
import { useSelector } from 'react-redux';
import { Button, message } from '@insight-health-ai/roseate/components';
import { Link } from 'react-router-dom';
import { analytics } from 'src/services';

const apptRenamePopupTourSeenKey = 'appt_rename_popup_tour_seen';

export const useCareConnectSideMenus = () => {
  const { user } = useAuth();
  const [showApptRenamePopupTour, setShowApptRenamePopupTour] = useState(
    getFromStorage(apptRenamePopupTourSeenKey) !== 'true'
  );
  const { currentPractitionerSettings } = useSelector(
    (state) => state?.practitionerState
  );

  const { isEntitled: hasIntakeAccess } = useFeatureEntitlements('intake');
  const { isEntitled: hasFollowupAccess } = useFeatureEntitlements('followup');

  // Don't show appointments tab is both intake access and followup access are false
  if (!hasIntakeAccess && !hasFollowupAccess) {
    return [];
  }

  const showLayoutSwitchMessage = () => {
    message.success({
      content: (
        <div className="flex items-center gap-2">
          <div className="flex flex-col text-left">
            <span className="font-medium">
              Successfully changed to the new layout!
            </span>
            <span className="text-xs text-gray-500">
              You can always switch back from{' '}
              <Link
                to="/settings"
                state={{
                  highlightElementSelector: '.layout-version-switcher',
                }}
                className="text-primary underline"
                onClick={() => {
                  message.destroy('layout-switch-success');
                  analytics.track(
                    'Clicked Settings Link in The Layout Switcher Success Message',
                    {
                      email: user?.email,
                      currentVersion:
                        currentPractitionerSettings?.sidenav_preferences
                          ?.version,
                    }
                  );
                }}
              >
                Settings → General
              </Link>
              .
            </span>
          </div>
          <Button
            appearance="primary"
            size="sm"
            onClick={() => {
              message.destroy('layout-switch-success');
              analytics.track(
                'Clicked Okay in The Layout Switcher Success Message',
                {
                  email: user?.email,
                  currentVersion:
                    currentPractitionerSettings?.sidenav_preferences?.version,
                }
              );
            }}
          >
            Okay
          </Button>
        </div>
      ),
      duration: 5,
      key: 'layout-switch-success',
    });
    analytics.track('Viewed The Layout Switcher Success Message', {
      email: user?.email,
      currentVersion: currentPractitionerSettings?.sidenav_preferences?.version,
    });
  };

  const isV2 = currentPractitionerSettings?.sidenav_preferences?.version === 2;

  const menus = [
    {
      path: '/appointments',
      title: isV2 ? 'Visits' : 'Appointments',
      icon: <ScheduleOutlined />,
      tourPopup: isV2
        ? {
            open: showApptRenamePopupTour,
            onOpenChange: () => {},
            title: (
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-2">
                  Appointments <ArrowNarrowRight /> Visits
                </span>
              </div>
            ),
            content:
              'Appointments is now renamed to Visits. You can still access your appointments by clicking the Visits tab.',
            primaryAction: {
              label: 'Okay',
              onClick: () => {
                analytics.track(
                  'Clicked Okay in The Appointments Tab Rename Popup',
                  {
                    email: user?.email,
                    currentVersion:
                      currentPractitionerSettings?.sidenav_preferences?.version,
                  }
                );
                setShowApptRenamePopupTour(false);
                storeInLocal(apptRenamePopupTourSeenKey, 'true');
                showLayoutSwitchMessage();
              },
            },
          }
        : undefined,
    },
  ];

  return menus;
};

export const useSettingsSideMenus = () => {
  const { isSuperAdmin } = useGetCurrentPractitioner();

  const { isEntitled: isOPHqAccount } = useFeatureEntitlements('op-hq-account');
  const { isEntitled: isOPHqSuperAdmin } = usePermissionEntitlements(
    'op-hq-invite-practitioner'
  );
  const { isEntitled: isOpenDrHqAccount } =
    useFeatureEntitlements('open-dr-hq-account');
  const { isEntitled: isOpenDrHqSuperAdmin } = usePermissionEntitlements(
    'open-dr-hq-invite-practitioner'
  );
  const { isEntitled: isPhoneTriageModuleEntitled } =
    useFeatureEntitlements('phone-triage');
  const { isEntitled: hasCallReadAccess } =
    usePermissionEntitlements('calls.read');
  const { isEntitled: hasReferralsAccess } = useFeatureEntitlements('referral');

  const hasTagAccess =
    hasReferralsAccess ||
    isSuperAdmin ||
    (isPhoneTriageModuleEntitled && hasCallReadAccess);

  const settingsMenus = [
    {
      path: '/settings',
      title: 'Settings',
      icon: <SettingOutlined />,
      submenu: [
        ...(isOPHqAccount && isOPHqSuperAdmin
          ? [
              {
                path: '/op-settings',
                title: 'OP Admin',
                icon: (
                  <img
                    src="https://images.insighthealth.ai/office-practicum/logo.svg"
                    alt="Office Practicum Logo"
                    width={20}
                    height={20}
                    className="mr-[-4px]"
                  />
                ),
              },
            ]
          : []),
        ...(isOpenDrHqAccount && isOpenDrHqSuperAdmin
          ? [
              {
                path: '/open-dr-settings',
                title: 'OpenDr Admin',
                icon: <SettingOutlined />,
              },
            ]
          : []),
      ],
    },
  ];

  if (hasTagAccess) {
    settingsMenus[0].submenu.push({
      path: '/settings/tags',
      title: 'Tags',
      icon: <TagsOutlined />,
    });
  }

  return settingsMenus;
};
